import { MusicalTimeline, TimeSignature } from "@/lib/music/musical-timeline";
import { Timeline } from "@/lib/tools/timeline";

export type GridLineKind = 'bar' | 'beat' | 'subdiv';

export type GridLine = {
  tick: number;
  kind: GridLineKind;
  /** Numéro de la mesure, 1-based — celui que le reste de l'app affiche. */
  barNumber: number;
};

/**
 * Où tombent les mesures, les temps et les demi-temps de la fenêtre visible.
 *
 * Le modèle, séparé de ceux qui le dessinent, parce qu'ils sont deux : les
 * lignes qui traversent le roll (`Measures`) et la règle graduée de son bord
 * gauche (`MeasureRuler`). Deux parcours de la timeline par image pour la même
 * réponse, ce serait deux fois le calcul — et surtout deux endroits où la
 * position d'une barre de mesure peut se mettre à diverger.
 *
 * Le cache tient une fenêtre exacte, et non un intervalle élargi : pendant la
 * lecture `toTick` avance à chaque image, donc il rate à chaque image. C'est le
 * comportement d'origine et il tient : le parcours ne visite que les quelques
 * secondes visibles.
 */
export class MeasureGrid {
  private timeSignatures: Timeline<{ tick: number, ts: TimeSignature }>;

  private lines: GridLine[] = [];
  private cachedFromTick = -1;
  private cachedToTick = -1;

  constructor(private musicalTimeline: MusicalTimeline) {
    this.timeSignatures = new Timeline();
    this.buildTimeSignatures();
  }

  setMusicalTimeline(musicalTimeline: MusicalTimeline): void {
    this.musicalTimeline = musicalTimeline;
    this.buildTimeSignatures();
    this.invalidate();
  }

  invalidate(): void {
    this.cachedFromTick = -1;
    this.cachedToTick = -1;
    this.lines.length = 0;
  }

  /**
   * Les lignes couvrant `[fromTick, toTick]`.
   *
   * Le tableau rendu appartient à la grille et est réécrit au prochain calcul :
   * on le lit dans l'image, on ne le garde pas. Il peut déborder de la fenêtre
   * demandée quand le cache vient d'un cadrage plus large — à l'appelant de
   * filtrer sur ce qu'il dessine.
   */
  linesFor(fromTick: number, toTick: number): readonly GridLine[] {
    if (fromTick < this.cachedFromTick || toTick > this.cachedToTick) {
      this.compute(fromTick, toTick);
    }

    return this.lines;
  }

  private compute(fromTick: number, toTick: number): void {
    this.lines.length = 0;
    this.cachedFromTick = fromTick;
    this.cachedToTick = toTick;

    let currentTick = fromTick;

    while (currentTick < toTick) {
      // When currentTick is before the first time signature (e.g. fromTick
      // negative at song start), getIndexAt returns -1. Fall back to the
      // first TS so the visible grid before tick 0 still aligns.
      let tsIndex = this.timeSignatures.getIndexAt(currentTick);

      if (tsIndex < 0) {
        tsIndex = 0;
      }

      const ts = this.timeSignatures.getByIndex(tsIndex);

      if (!ts) {
        break;
      }

      const nextTs = this.timeSignatures.getByIndex(tsIndex + 1);
      const segmentEnd = Math.min(nextTs?.tick ?? Infinity, toTick);

      const ticksPerBeat = this.musicalTimeline.getTicksPerBeat(ts.tick);
      const ticksPerBar = this.musicalTimeline.getTicksPerBar(ts.tick);
      const ticksPerSubdiv = ticksPerBeat / 2;

      // True modulo in [0, ticksPerSubdiv), JS `%` returns a negative
      // remainder for negative operands, which would skip past tick 0.
      const offset = currentTick - ts.tick;
      const remainder = ((offset % ticksPerSubdiv) + ticksPerSubdiv) % ticksPerSubdiv;

      if (remainder !== 0) {
        currentTick += ticksPerSubdiv - remainder;
      }

      while (currentTick < segmentEnd) {
        const offsetInBar = (currentTick - ts.tick) % ticksPerBar;
        const offsetInBeat = (currentTick - ts.tick) % ticksPerBeat;

        let kind: GridLineKind;

        if (offsetInBar === 0) {
          kind = 'bar';
        } else if (offsetInBeat === 0) {
          kind = 'beat';
        } else {
          kind = 'subdiv';
        }

        this.lines.push({
          tick: currentTick,
          kind,
          barNumber: this.barNumberAt(currentTick),
        });

        currentTick += ticksPerSubdiv;
      }
    }
  }

  /**
   * Le numéro de mesure tel que le studio le compte partout ailleurs.
   *
   * On passe par `getBarAtTick` plutôt que par le compte du parcours ci-dessus
   * : la règle affiche le même numéro que la barre d'outils et que ce que
   * `StudioView` publie à React, et c'est cette concordance qui compte pour le
   * lecteur. L'arrondi précède le calcul parce que `getBarAtTick` termine par
   * un `Math.floor` : un tick de ligne à `débutDeMesure - ε` rendrait la mesure
   * précédente.
   */
  private barNumberAt(tick: number): number {
    return this.musicalTimeline.getBarAtTick(Math.round(tick)) + 1;
  }

  private buildTimeSignatures(): void {
    this.timeSignatures = new Timeline();

    this.musicalTimeline.getTimeSignatures().forEach(ts => {
      this.timeSignatures.set(ts.tick, ts);
    });
  }
}
